/** Einstellungen: Mitteilungen, Zeiten, Kurzbefehle/NFC, Kalender, Sicherung, Diagnose. */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import type { LinkInfo, Settings } from '@pflanzen/shared';
import { api, path } from '../lib/api';
import { mutate, useStore } from '../lib/store';
import { enablePush, permission, pushSupport, sendTestPush, isStandalone } from '../lib/push';
import { Button, Card, SectionTitle, Toggle } from '../ui/kit';

const field = 'min-h-11 w-30 rounded-xl border border-line bg-paper px-3 text-right text-[16px]';
const halfField = 'min-h-11 w-[4.5rem] rounded-xl border border-line bg-paper px-1.5 text-center text-[16px]';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-3">
      <span className="text-[15px]">{label}</span>
      {children}
    </div>
  );
}

export function SettingsPage() {
  const { state } = useStore();
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  const [perm, setPerm] = useState(permission());
  const [links, setLinks] = useState<LinkInfo | null>(null);
  useEffect(() => {
    api<LinkInfo>('GET', path('links')).then(setLinks).catch(() => setLinks(null));
  }, []);
  if (!state) return null;
  const s = state.settings;
  const save = (patch: Partial<Settings>) => void mutate('PATCH', path('settings'), patch);
  const saveNotify = (patch: Partial<Settings['notify']>) => save({ notify: { ...s.notify, ...patch } });

  // Wichtig: direkt im Fingertipp aufrufen (iOS verlangt das für die Erlaubnis)
  const onEnable = () => {
    setPushMsg('Einen Moment …');
    enablePush()
      .then((r) => {
        setPushMsg(r.message);
        setPerm(permission());
      })
      .catch((e: unknown) => setPushMsg(e instanceof Error ? e.message : String(e)));
  };
  const onTest = () => {
    setPushMsg('Sende Test …');
    sendTestPush()
      .then((r) => setPushMsg(r.ok ? 'Test verschickt – kommt gleich an.' : `Nicht angekommen: ${r.error ?? 'kein Abo'}`))
      .catch((e: unknown) => setPushMsg(e instanceof Error ? e.message : String(e)));
  };

  const support = pushSupport();
  return (
    <div className="px-4 pb-8">
      <header className="pt-safe pb-2">
        <h1 className="font-display text-[34px] leading-tight">Einstellungen</h1>
      </header>

      <SectionTitle>Mitteilungen</SectionTitle>
      <Card className="space-y-3">
        {support === 'not-installed' && (
          <p className="rounded-2xl bg-sun-soft p-3 text-sm">
            Mitteilungen gehen auf dem iPhone nur in der installierten App: in Safari <b>Teilen → Zum Home-Bildschirm</b>, dann die App von dort öffnen.
          </p>
        )}
        {support === 'ok' && perm !== 'granted' && (
          <Button onClick={onEnable} className="w-full">
            🔔 Mitteilungen aktivieren
          </Button>
        )}
        {perm === 'granted' && (
          <div className="flex items-center justify-between">
            <span className="text-[15px]">✅ Mitteilungen aktiv</span>
            <Button tone="ghost" onClick={onTest} className="min-h-10 py-2">
              Test senden
            </Button>
          </div>
        )}
        {pushMsg && <p className="text-sm text-muted">{pushMsg}</p>}
        <Row label="Morgens erinnern um">
          <input type="time" className={field} value={s.morningTime} onChange={(e) => e.target.value && save({ morningTime: e.target.value })} />
        </Row>
        <Row label="Nochmal erinnern um">
          <input
            type="time"
            className={field}
            value={s.followUpTime ?? ''}
            onChange={(e) => save({ followUpTime: e.target.value || null })}
          />
        </Row>
        <p className="-mt-2 text-xs text-muted">Nur wenn bis dahin noch etwas offen ist. Leer = aus.</p>
        <Row label="Ruhezeit">
          <span className="flex items-center gap-1.5">
            <input type="time" className={halfField} value={s.quietStart} onChange={(e) => e.target.value && save({ quietStart: e.target.value })} />
            –
            <input type="time" className={halfField} value={s.quietEnd} onChange={(e) => e.target.value && save({ quietEnd: e.target.value })} />
          </span>
        </Row>
        <Row label="Untersetzer-Erinnerung nach">
          <select className={field} value={s.saucerMinutes} onChange={(e) => save({ saucerMinutes: Number(e.target.value) })}>
            <option value={0}>aus</option>
            <option value={30}>30 Min</option>
            <option value={60}>1 Stunde</option>
            <option value={120}>2 Stunden</option>
          </select>
        </Row>
        <Toggle label="⛈️ Unwetter bei offenem Fenster" checked={s.notify.weatherAlerts} onChange={(v) => saveNotify({ weatherAlerts: v })} />
        <Toggle label="☀️ Pralle Sonne → Vorhang zu" checked={s.notify.sun} onChange={(v) => saveNotify({ sun: v })} />
        <Toggle label="🥶 Kalte Zugluft" checked={s.notify.draft} onChange={(v) => saveNotify({ draft: v })} />
        <Toggle label="🌧️ Lüftungs-Tipps (milder Regen)" checked={s.notify.airingTips} onChange={(v) => saveNotify({ airingTips: v })} />
        <Toggle label="🌙 Unwetter auch in der Ruhezeit" checked={s.notify.alertsInQuiet} onChange={(v) => saveNotify({ alertsInQuiet: v })} />
      </Card>

      <SectionTitle>Allgemein</SectionTitle>
      <Card className="space-y-1">
        <Row label="Dein Name">
          <input className={field} defaultValue={s.userName} onBlur={(e) => save({ userName: e.target.value.trim() })} />
        </Row>
        <Row label="Zimmertemperatur (°C)">
          <input
            className={field}
            inputMode="decimal"
            defaultValue={s.indoorTempC}
            onBlur={(e) => {
              const v = Number(e.target.value.replace(',', '.'));
              if (v >= 10 && v <= 35) save({ indoorTempC: v });
            }}
          />
        </Row>
        <p className="pb-2 text-xs text-muted">Für die Luftfeuchte-Schätzung drinnen. Wetter-Ort: {s.location.name}</p>
      </Card>

      <SectionTitle>Mehr</SectionTitle>
      <Card className="divide-y divide-line py-0">
        {[
          { to: '/einstellungen/kurzbefehle', icon: '📲', label: 'NFC-Sticker & Siri', hint: 'Abhaken ohne App' },
          { to: '/einstellungen/kalender', icon: '📅', label: 'Kalender-Abo', hint: links ? 'Termine im iPhone-Kalender' : '' },
          { to: '/einstellungen/sicherung', icon: '💾', label: 'Sicherung & Geräte', hint: 'Export, Wiederherstellen' },
          { to: '/einstellungen/diagnose', icon: '🩺', label: 'Diagnose', hint: 'Klappt alles?' },
        ].map((item) => (
          <Link key={item.to} to={item.to} className="flex min-h-14 items-center gap-3 py-2">
            <span className="w-7 shrink-0 text-center text-xl">{item.icon}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px]">{item.label}</span>
              {item.hint && <span className="block text-xs text-muted">{item.hint}</span>}
            </span>
            <ChevronRight className="h-4 w-4 text-muted" />
          </Link>
        ))}
      </Card>
      <p className="mt-6 text-center text-xs text-muted">
        Pflanzen · {isStandalone() ? 'installiert' : 'im Browser'} · v{__APP_VERSION__}
      </p>
    </div>
  );
}
